<template lang="html">
    <section class="user-list">
        <div>
            <div class="user-header">
                <h1 class="user-header-title">{{ $t("userList.title") }}</h1>
            </div>

            <Loader :loading="loading"></Loader>

            <div v-if="userList.length" class="row d-flex align-items-center mb-5">
                <div class="col d-flex align-items-center">
                    <input type="text" id="searchBySearch" v-model.trim="search" class="rem3rch-input-text" />
                    <label for="searchBySearch" class="m-0 pointer ml-3"><i class="fa fa-search fa-lg color-grey"></i></label>

                    <div class="ml-5">
                        <label for="checkboxUsers" class="label-checkbox pointer m-0 d-flex align-items-center">
                            <input type="checkbox" id="checkboxUsers" class="d-none" v-model="checked.checkedUsers" @change="optionsChecked" />
                            <span v-show="checked.checkedUsers" class="rem3rch-checkbox rem3rch-checkbox"> <i class="fa fa-check"></i> </span>
                            <span v-if="!checked.checkedUsers" class="rem3rch-checkbox rem3rch-checkbox"></span>
                            <span class="label-title">{{ $trad.userList.viewActiveOnly }}</span>
                        </label>
                    </div>

                    <div v-if="hasAccessSuperAdmin()" class="ml-5">
                        <label for="checkboxBrands" class="label-checkbox pointer m-0 d-flex align-items-center">
                            <input type="checkbox" id="checkboxBrands" class="d-none" v-model="checked.checkedBrands" @change="optionsChecked" />
                            <span v-show="checked.checkedBrands" class="rem3rch-checkbox rem3rch-checkbox"> <i class="fa fa-check"></i> </span>
                            <span v-if="!checked.checkedBrands" class="rem3rch-checkbox rem3rch-checkbox"></span>
                            <span class="label-title">{{ $trad.userList.activateAllBrands }}</span>
                        </label>
                    </div>
                </div>
                <div class="col-auto">
                    <div class="user-header-adduser">
                        <button v-if="canAddUser()" @click="$router.push({ path: `/users/create` })" class="btn btn-adduser">
                            <i class="fa fa-plus fa-xs"></i> <span class="ml-2">{{ $t("actions.addUser") }}</span>
                        </button>
                    </div>
                </div>
            </div>

            <div class="table-responsive">
                <table class="user-table">
                    <thead>
                        <tr>
                            <th class="pointer" @click="orderBy('id')"><span>ID</span></th>
                            <th class="pointer" @click="orderBy('firstname')">
                                <span>{{ $trad.createUser.labelFirstName }}</span>
                            </th>
                            <th class="pointer" @click="orderBy('lastname')">
                                <span>{{ $trad.createUser.labelLastName }}</span>
                            </th>
                            <th class="pointer" @click="orderBy('email')">
                                <span>{{ $trad.userList.email }}</span>
                            </th>
                            <th v-if="hasAccessSuperAdmin()">
                                <span>{{ $trad.userList.brand }}</span>
                            </th>
                            <th v-if="hasAccessSuperAdmin()">
                                <span>{{ $trad.userList.remerchAccess }}</span>
                            </th>
                            <th class="pointer" @click="orderBy('lastConnection')">
                                <span>{{ $trad.userList.lastConnection.toLocaleString() }}</span>
                            </th>
                            <th><span>SSO</span></th>
                            <th>
                                <span>{{ $trad.common.edit }}</span>
                            </th>
                            <th v-if="hasAccessSuperAdmin()">
                                <span>{{ $trad.common.delete }}</span>
                            </th>
                            <th>
                                <span>{{ $trad.common.history }}</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr class="notfound" v-if="!userListBySearch.length">
                            <td :colspan="hasAccessSuperAdmin() ? 10 : 9">{{ $trad.userrole.notfound }}</td>
                        </tr>
                        <tr v-for="(user, index) in userListBySearch" :key="index">
                            <td class="id">
                                <span>{{ user.id }}</span>
                            </td>
                            <td class="firstname">
                                <span>{{ user.firstname }}</span>
                            </td>
                            <td class="lastname">
                                <span>{{ user.lastname }}</span>
                            </td>
                            <td class="email">
                                <span>{{ user.email }}</span>
                            </td>
                            <td v-if="hasAccessSuperAdmin()" class="brands">
                                <span
                                    v-for="userBrand in user.userBrand"
                                    class="allBrands"
                                    :class="userBrand.brand.name.toLowerCase()"
                                    :style="'background-color: ' + userBrand.brand.linkColor + ';'"
                                    v-if="!user.isSuperAdmin"
                                    :key="userBrand.id"
                                >
                                    <strong @click="openModalDeleteBrand(user, userBrand.brand.id)">X</strong> {{ userBrand.brand.name }}
                                </span>
                            </td>
                            <td v-if="hasAccessSuperAdmin()" class="checkbox-elm">
                                <label :for="'checkbox' + user.id" class="label-checkbox m-0 pointer">
                                    <input
                                        class="d-none"
                                        type="checkbox"
                                        :id="'checkbox' + user.id"
                                        v-model="user.isEnabled"
                                        @change="openModalChangeAccess(user)"
                                    />
                                    <span v-show="user.isEnabled" class="rem3rch-checkbox rem3rch-checkbox"> <i class="fa fa-check"></i> </span>
                                    <span v-if="!user.isEnabled" class="rem3rch-checkbox rem3rch-checkbox"></span>
                                </label>
                            </td>
                            <td class="lastConnection">
                                <span v-if="user.lastConnectionDate"> {{ getDateByFormat(user.lastConnectionDate, "DD/MM/YYYY HH:mm") }} </span>
                                <span v-else>-</span>
                            </td>
                            <td class="sso">
                                <span v-if="user.isSsoEnabled"><i class="fa fa-check color-grey"></i></span>
                            </td>
                            <td class="edit">
                                <router-link :to="{ name: 'useraccess', params: { id: user.id, checkedOption: checked } }">
                                    <img class="position-relative" src="../../assets/edit.svg" alt="edit" />
                                </router-link>
                            </td>
                            <td class="delete" v-if="hasAccessSuperAdmin()">
                                <img
                                    @click="openModalDeleteBrand(user, currentBrand.id)"
                                    class="position-relative pointer"
                                    src="../../assets/delete.svg"
                                    alt="delete"
                                />
                            </td>
                            <td class="history"><History :object="user" :object-type="OBJECT_TYPE.USERS"></History></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        <DeleteBrand :params="deleteBrandParams" @loadUsers="loadUsers"></DeleteBrand>
        <ChangeAccess :params="updateAccessParams" @loadUsers="loadUsers" @resetState="resetState"></ChangeAccess>
    </section>
</template>

<script lang="js">
import {mapActions, mapGetters, mapState} from 'vuex'
import Modal from './modal/Modal'
import History from "@/components/history/History";
import {OBJECT_TYPE} from '@/components/history/constants';
import DeleteBrand from "@/components/users/actions/DeleteBrand";
import Loader from "@/components/loader/loader";
import ChangeAccess from "@/components/users/actions/ChangeAccess";
import {removeNbsp} from "../../utils/string";


export default {
    name: 'user-list',
    props: [],
    components: {
        ChangeAccess,
        Loader,
        DeleteBrand,
        History,
        Modal
    },
    data () {
        return {
            dataAction: '',
            search: "",
            checked: {
                checkedBrands: false,
                checkedUsers: true
            },
            userList: [],
            OBJECT_TYPE,
            deleteBrandParams: {},
            updateAccessParams: {},
            loading: true
        }
    },
    methods: {
        ...mapActions('user', {
            getAllUsersListAction: 'getAllUsersListAction'
        }),
        optionsChecked () {
            this.loadUsers();
        },
        openModalChangeAccess (user) {
            this.updateAccessParams = {
                user: user,
                isActive: true
            }
        },
        openModalDeleteBrand (user, brandId) {
            const userBrand  = user.userBrand.find(ub => ub.brand.id === brandId);
            this.deleteBrandParams = {
                user: user,
                userBrand: userBrand,
                isActive: true
            };
        },
        orderBy(field){
            if(field === "lastConnection"){
               this.userList.sort(function(a,b){
                   return new Date(b.lastConnectionDate) - new Date(a.lastConnectionDate)
               });
            }
            if(["id", "firstname", "lastname", "email"].find(element => element === field)){
                if("id" === field){
                    this.userList.sort(function(a,b){
                        return b.id > a.id ? -1 : 1
                    });
                }
                if("firstname" === field){
                    this.userList.sort(function(a,b){
                        return b.firstname > a.firstname ? -1 : 1
                    });
                }
                if("lastname" === field){
                    this.userList.sort(function(a,b){
                        return b.lastname > a.lastname ? -1 : 1
                    });
                }
                if("email" === field){
                    this.userList.sort(function(a,b){
                        return b.email > a.email ? -1 : 1
                    });
                }

            }
        },
        loadUsers() {
            this.loading = true;
            this.getAllUsersListAction(this.checked).then(() => {
                this.userList = this.getAllUsersList;
                this.loading = false;
            });
        },
        resetState(user) {
            const searchUser = this.userList.find(u => u.id === user.id);
            if (searchUser) {
                searchUser.isEnabled = !user.isEnabled;
            }
        },
        canAddUser() {
            return this.isSuperAdmin() || this.isClientAdmin() || this.hasAccessValidate('user_manager', [6,7]);
        }
    },
    created () {
        this.loadUsers();
    },
    computed: {
        ...mapGetters('user', {
            getAllUsersList: 'getAllUsersList'
        }),
        ...mapState("currentUser", ["currentBrand"]),
        userListBySearch(){
            return this.userList.filter( u => {
                this.search = removeNbsp(this.search);
                return this.search === "" ||
                    (u.id+'').indexOf(this.search) > -1 ||
                    removeNbsp(u.email).toLowerCase().indexOf(this.search.toLowerCase()) > -1 ||
                    removeNbsp(u.lastname).toLowerCase().indexOf(this.search.toLowerCase()) > -1 ||
                    removeNbsp(u.firstname).toLowerCase().indexOf(this.search.toLowerCase()) > -1
            })
        }
    }
}
</script>

<style scoped lang="scss">
.user {
    &-header {
        margin: 20px 0;
        &-title {
            color: #6f6f6f;
            font-size: 30px;
            margin: 0;
            text-transform: uppercase;
            font-family: "Oswald", sans-serif;
            font-weight: normal;
            display: inline-block;
        }
        &-adduser {
            display: inline-block;
            .btn-adduser {
                padding: 10px 25px;
                background-color: #000;
                color: #fff;
                span {
                    text-transform: uppercase;
                    font-size: 14px !important;
                    font-family: "Roboto", sans-serif;
                }
            }
        }
    }
    &-list {
        background: white;
        width: 100%;
        padding: 20px;
    }
    &-table {
        border-collapse: collapse;
        width: 100%;
        font-size: 12px;
        tr {
            &.notfound {
                height: 150px;
                td {
                    border: #343434 1px solid;
                    background-color: #fff;
                }
            }
        }
        th {
            font-family: "Oswald", sans-serif;
            color: white;
            background: #343434;
            border: #343434 1px solid;
            text-transform: uppercase;
            text-align: center;
            font-weight: normal;
            vertical-align: center;
            padding: 15px 10px;
            span {
                font-size: 19px;
                @media (max-width: 1440px) {
                    font-size: 16px;
                }
            }
        }
        td {
            border-bottom: 5px solid #fff;
            font-size: 18px;
            line-height: 18px;
            padding: 10px;
            color: #262626;
            background-color: #f4f4f4;
            text-align: center;

            &.id {
                width: 90px;
            }

            &.firstname,
            &.lastname {
                span {
                    font-size: 18px;
                    font-family: "Oswald", sans-serif;
                    @media (max-width: 1440px) {
                        font-size: 16px;
                    }
                }
            }

            &.email,
            &.brands {
                text-align: left;
                width: 350px;
                span {
                    text-align: left;
                    text-transform: inherit;
                    &.allBrands {
                        text-transform: uppercase;
                    }
                }
            }

            &.id,
            &.lastConnection,
            &.email {
                span,
                i {
                    font-family: "oswald-regular", Sans-Serif;
                    font-size: 16px;
                    @media (max-width: 1440px) {
                        font-size: 14px;
                    }
                }
            }

            &.sso,
            &.edit,
            &.delete,
            &.history {
                width: 100px;
                span {
                    font-size: 22px;
                }
                @media (max-width: 1440px) {
                    width: 70px;
                    img {
                        width: 35px;
                    }
                }
            }
        }
        .checkbox-elm {
            text-align: center;
        }
        .edit-user {
            svg {
                color: #2d2d2d;
                &:hover {
                    color: #6f6f6f;
                }
            }
        }
        .roleUser {
            background: black;
            font-size: 12px;
            padding: 4px;
            color: white;
            font-weight: bold;
            display: block;
            text-align: center;
            &.rules {
                &-sa {
                    background: #8dce00;
                }
                &-c {
                    background: #ffc817;
                }
                &-d {
                    background: #41b4e8;
                }
            }
        }
        .allBrands {
            /*background: #3f3f3f;*/
            color: #ffffff;
            border-radius: 10px;
            display: inline-block;
            padding: 5px 10px;
            font-size: 11px;
            margin: 3px 5px;
            strong {
                font-size: 10px;
                margin-right: 5px;
                cursor: pointer;
            }
        }
    }
}
.color-grey {
    color: #6f6f6f !important;
}
</style>



// WEBPACK FOOTER //
// UserList.vue?d580db5e